import type { ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { GraduationCap } from 'lucide-react';

import { canonicalCrmHref } from '../../../supabase/functions/_shared/crmFiche';

/** Un lien `aq:tour?id=<clé>` cité par Otto (visite guidée du produit client). */
const TOUR_LINK = /^aq:tour\?id=[a-z0-9-]+/i;

interface MarkdownProps {
  /** Raw markdown text. Renders nothing when empty/blank. */
  children: string | null | undefined;
  /** Extra classes merged onto the prose wrapper. */
  className?: string;
  /** Invert prose colors for messages sitting on a dark/colored bubble. */
  invert?: boolean;
  /**
   * Opt-in : rend les cases à cocher GFM (`- [ ]`) cliquables. Le callback reçoit
   * l'index 0-based de la case dans l'ordre du document (à mapper avec
   * `toggleTaskAtIndex`). Absent → cases en lecture seule (comportement par défaut,
   * inchangé pour les autres surfaces qui réutilisent ce composant).
   */
  onToggleTask?: (index: number) => void;
  /**
   * Opt-in : rend autrement un lien interne (`/admin/...`) — le Bureau y montre l'aperçu d'un
   * contact, d'un compte ou d'une opportunité. Renvoie null pour garder le lien ordinaire.
   */
  renderInternalLink?: (href: string, label: ReactNode) => ReactNode | null;
}

/**
 * Single source of truth for rendering assistant/markdown messages across the
 * admin (inbox, Otto sidebar, widget preview, workflow feedback). Built on
 * `react-markdown` + `remark-gfm` (same engine as the KB), which never renders
 * raw HTML — so it's XSS-safe by default, no sanitizer needed.
 *
 * Internal links (`/admin/...`) navigate via the SPA router; external links
 * open in a new tab. A link to a contact's or an account's older CRM page (written in a message
 * before the fiches) opens the fiche (`_shared/crmFiche.ts`). User-typed (plain) messages should NOT go through here —
 * render them as text, mirroring the widget (`user = textContent`).
 */
export function Markdown({ children, className, invert = false, onToggleTask, renderInternalLink }: MarkdownProps) {
  const navigate = useNavigate();
  const content = (children ?? '').trim();
  if (!content) return null;

  // Compteur réinitialisé à chaque rendu : react-markdown rend les cases dans
  // l'ordre du document, donc l'index attribué ici correspond à l'ordre source.
  let taskIndex = -1;

  const components: Components = {
    a({ href, children: label }) {
      const url = href ?? '';
      // Tutoriel interactif cité par Otto : chip informatif (les visites guidées se
      // jouent chez le client via le widget — pas lançable depuis le back-office).
      if (TOUR_LINK.test(url)) {
        return (
          <span
            className="not-prose mx-0.5 inline-flex items-center gap-1.5 rounded-lg border border-blue-100 bg-[#f3f7ff] px-2 py-0.5 align-middle text-[13px] font-semibold text-[#0066FF] dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300"
            title="Tutoriel interactif (visite guidée côté client)"
          >
            <GraduationCap className="h-3.5 w-3.5 shrink-0" />
            <span>{label}</span>
          </span>
        );
      }
      if (url.startsWith('/')) {
        const internal = canonicalCrmHref(url);
        const custom = renderInternalLink?.(internal, label);
        if (custom) return custom;
        return (
          <a
            href={internal}
            onClick={(e) => {
              e.preventDefault();
              navigate(internal);
            }}
          >
            {label}
          </a>
        );
      }
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      );
    },
  };

  if (onToggleTask) {
    components.input = ({ node, ...props }) => {
      // `node` (hast) is destructured out so it isn't forwarded to the DOM input.
      if (node && props.type === 'checkbox') {
        const index = (taskIndex += 1);
        return (
          <input
            type="checkbox"
            checked={Boolean(props.checked)}
            onChange={() => onToggleTask(index)}
            className="not-prose mr-1.5 -translate-y-px cursor-pointer align-middle accent-[#0066FF]"
          />
        );
      }
      return <input {...props} />;
    };
  }

  return (
    <div
      className={twMerge(
        'prose prose-sm max-w-none break-words',
        'prose-p:my-2 first:prose-p:mt-0 last:prose-p:mb-0',
        'prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5',
        'prose-pre:bg-neutral-900 prose-pre:text-neutral-100 prose-pre:rounded-lg',
        'prose-a:text-[#0066FF] prose-a:no-underline hover:prose-a:underline',
        invert && 'prose-invert',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
